import React from "react";
import {
  FormControl, InputLabel, Select, MenuItem, TextField, Typography, CircularProgress
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { i18n } from "../../translate/i18n";
import { EMPTY_OPTION, toOption, fromOption } from "../../helpers/muiEmptyOption";

// ─── Plantilla Meta elegida para un paso de recordatorio (R-0855) ──────────
// Vive en components/ (no en pages/TurnosIA/) porque también lo usa el panel
// superadmin (R-0893): un import estático desde afuera del módulo TurnosIA
// metería código del módulo en el bundle principal (ver CLAUDE.md).
// Sin elección, el paso sale como siempre (texto de "Plantillas de mensajes" +
// plantilla por convención según el día). Con elección, sale ESA plantilla y
// cada {{n}} se llena con la variable que se mapea acá.
//
// Props:
//   value    → { name, language, variables: string[] } | null
//   onChange → (nuevoValor | null) => void
//   options  → respuesta de GET /company/reminder-templates
//              { whatsappId, templates: [...] | null, variables: [...], datoKeys }
//   loading  → bool
//
// La validación de verdad (aprobada en la línea, cantidad de variables,
// variables conocidas) la hace el backend al guardar.

const keyOf = (t) => (t ? `${t.name}|${t.language}` : "");

// Meta cobra distinto UTILITY y MARKETING: se muestra para poder elegir.
const categoryLabel = (c) =>
  i18n.exists(`reminderStepTemplate.category.${c}`) ? i18n.t(`reminderStepTemplate.category.${c}`) : c;

const ReminderStepTemplateSection = ({ value, onChange, options, loading }) => {
  const templates = options?.templates;
  const variables = options?.variables || [];
  const selected = value?.name
    ? (templates || []).find((t) => t.name === value.name && t.language === value.language) || null
    : null;

  const handleTemplate = (raw) => {
    const k = fromOption(raw);
    if (!k) { onChange(null); return; }
    const t = (templates || []).find((x) => keyOf(x) === k);
    if (!t) return;
    // Conserva lo ya mapeado si la cantidad coincide; si no, arranca vacío.
    const prev = Array.isArray(value?.variables) ? value.variables : [];
    const vars = Array.from({ length: t.variableCount }, (_, i) => prev[i] || "");
    onChange({ name: t.name, language: t.language, variables: vars });
  };

  const handleVar = (idx, v) => {
    const vars = [...(value?.variables || [])];
    vars[idx] = (v || "").trim();
    onChange({ ...value, variables: vars });
  };

  if (loading) return <CircularProgress size={20} />;

  return (
    <div style={{ marginTop: 8 }}>
      <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
        {i18n.t("reminderStepTemplate.title")}
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
        {i18n.t("reminderStepTemplate.help")}
      </Typography>

      {templates === null || templates === undefined ? (
        <Typography variant="caption" color="error" style={{ display: "block" }}>
          {i18n.t("reminderStepTemplate.unavailable")}
        </Typography>
      ) : (
        <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
          <InputLabel shrink>{i18n.t("reminderStepTemplate.templateLabel")}</InputLabel>
          <Select
            value={toOption(value?.name ? keyOf(value) : "")}
            onChange={(e) => handleTemplate(e.target.value)}
            label={i18n.t("reminderStepTemplate.templateLabel")}
            notched
          >
            <MenuItem value={EMPTY_OPTION}>{i18n.t("reminderStepTemplate.automatic")}</MenuItem>
            {templates.map((t) => (
              <MenuItem key={keyOf(t)} value={keyOf(t)}>
                {t.name} ({t.language}) · {i18n.t("reminderStepTemplate.varCount", { count: t.variableCount })}
                {t.category ? ` · ${categoryLabel(t.category)}` : ""}
              </MenuItem>
            ))}
            {/* Elegida antes y ya no aprobada: se muestra para que se vea y se pueda cambiar */}
            {value?.name && !selected && (
              <MenuItem value={keyOf(value)} disabled>
                {value.name} ({value.language}) — {i18n.t("reminderStepTemplate.notApproved")}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}

      {templates && templates.length === 0 && (
        <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
          {i18n.t("reminderStepTemplate.noneApproved")}
        </Typography>
      )}

      {value?.name && (
        <>
          {selected?.bodyText && (
            <Typography
              variant="body2"
              style={{
                whiteSpace: "pre-wrap", background: "#f5f5f5", borderRadius: 6,
                padding: "8px 10px", margin: "4px 0 10px", fontSize: 12
              }}
            >
              {selected.bodyText}
            </Typography>
          )}
          {(value.variables || []).map((v, idx) => (
            <Autocomplete
              key={idx}
              freeSolo
              options={variables}
              value={v || ""}
              onChange={(_e, nv) => handleVar(idx, nv)}
              onInputChange={(_e, nv, reason) => { if (reason === "input") handleVar(idx, nv); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`{{${idx + 1}}}`}
                  variant="outlined"
                  size="small"
                  placeholder={i18n.t("reminderStepTemplate.varPlaceholder")}
                  style={{ marginBottom: 8 }}
                />
              )}
            />
          ))}
          <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
            {i18n.t("reminderStepTemplate.varsHelp")}
          </Typography>
        </>
      )}
    </div>
  );
};

export default ReminderStepTemplateSection;
